import { normalizeCfItems } from './dynamicContentUtils';
import { MASTER_VARIATION, selectCardsForRegion } from './geoTargetingUtils';
import { CfNode, ExtendedCfNode } from '../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';
import { getProcessEnvs, logger } from '@marriott/mi-headless-utils';
const { log } = logger({})('fetchCFData');

const isMockCFDataEnabled = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const nextPublicMockCfData = process.env.NEXT_PUBLIC_USE_MOCK_CFDATA;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const mockCfData = process.env.USE_MOCK_CFDATA;
  return nextPublicMockCfData === 'true' || mockCfData === 'true';
};

/**
 * Server-side CF fetcher with Basic Auth support.
 * Can consume either mock data or real AEM GraphQL responses.
 *
 * @param tabName - The tab whose Content Fragments to fetch.
 * @param isAuthorMode - Whether AEM author mode is active.
 * @param region - Optional geo-targeting region. When provided, it is added to
 *   the GraphQL query so AEM returns the region-specific CF variation. When
 *   omitted, AEM returns the default (non-regional) variation.
 */
export async function fetchCFData(tabName: string, isAuthorMode: boolean, region?: string): Promise<CfNode[]> {
  try {
    log.info(`[fetchCFData] Start for tabName: ${tabName}${region ? `, region: ${region}` : ''}`);
    const isAuthorModeBool = String(isAuthorMode).toLowerCase() === 'true';
    log.info(`[fetchCFData] isAuthorMode: ${isAuthorModeBool}`);
    let nodes: ExtendedCfNode[] = [];

    if (isMockCFDataEnabled()) {
      // local mock import
      const { cfMockData } = await import('../organisms/DynamicTabbedLayout/cfMockContent.mock');
      nodes = selectCardsForRegion<ExtendedCfNode>(cfMockData[tabName as keyof typeof cfMockData]?.items, region);
      log.info(`[fetchCFData] CF nodes count from mock: ${nodes.length}`);
    } else {
      // real AEM GraphQL fetch
      const { NEXT_PUBLIC_AEM_PUBLISH } = getProcessEnvs();
      const AEM_GRAPHQL_BASE = !isAuthorModeBool ? NEXT_PUBLIC_AEM_PUBLISH : '';
      const AEM_GRAPHQL_QUERY_ROOT = process.env['AEM_GRAPHQL_QUERY_ROOT'] || 'mi-aem-common-spa/tabcollectionlist';
      const AEM_GRAPHQL_LOCALE = process.env['AEM_GRAPHQL_LOCALE'] || 'en-us';
      const AEM_GRAPHQL_BASIC_AUTH = process.env['NEXT_AEM_AUTH'];

      const tagsJson = JSON.stringify({
        _logOp: 'OR',
        _expressions: [
          {
            value: `${tabName.toLowerCase()}`,
            _operator: 'CONTAINS',
          },
        ],
      });

      // `region` travels as its own persisted-query filter param (sibling of
      // `tags`), using the same StringFilter JSON shape. Per the agreed
      // contract it is ALWAYS sent and carries the resolved region (when one
      // was resolved) plus the `master` fallback variation, both EQUALS/OR — so
      // AEM returns the region-specific variation when it exists and the
      // `master` base otherwise. Kept identical to the CSR UXL contract.
      const regionJson = JSON.stringify({
        _logOp: 'OR',
        _expressions: [
          ...(region ? [{ value: region, _operator: 'EQUALS' }] : []),
          { value: MASTER_VARIATION, _operator: 'EQUALS' },
        ],
      });
      const regionParam = `;region=${regionJson}`;

      const graphPath = `${AEM_GRAPHQL_QUERY_ROOT}${encodeURIComponent(
        `;tags=${tagsJson};locale=${AEM_GRAPHQL_LOCALE}${regionParam}`
      )}`;

      const url = `${AEM_GRAPHQL_BASE}/graphql/execute.json/${graphPath}`;
      log.info(`[fetchCFData] Fetching CF nodes from AEM url: ${url}`);

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (AEM_GRAPHQL_BASIC_AUTH) headers['Authorization'] = AEM_GRAPHQL_BASIC_AUTH;
      log.info(`[fetchCFData] headers: ${JSON.stringify(headers)}`);
      const aemRes = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!aemRes.ok) throw new Error(`[fetchCFData] AEM CF fetch failed: ${aemRes.status} ${aemRes.statusText}`);

      const json = await aemRes.json();
      // The response contains one entry per CF variation (master + any
      // region-specific variation). Pick the cards for the resolved region,
      // falling back to master, instead of blindly taking the first node.
      nodes = selectCardsForRegion<ExtendedCfNode>(json?.data?.tabCollectionList?.items, region);
      log.info(`[fetchCFData] CF nodes count from AEM: ${nodes.length} for tabName: ${tabName}`);
    }
    const normalized = normalizeCfItems(nodes, tabName);
    log.info(`[fetchCFData] Normalized CF nodes count: ${normalized.length} for tabName: ${tabName}`);
    return normalized;
  } catch (err) {
    log.error('[fetchCFData] Error fetching CF nodes:', err);
    return [];
  }
}
